import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../../Components/UI/dialog';
import { Button } from '../../../Components/UI/button';
import { X, ExternalLink, FileText, Loader2, ArrowLeft, Calendar } from 'lucide-react';
import { getSubscriptionData } from './SubscriptionInfoAPI';
import { useSelector } from 'react-redux';
import { getAgencyDetails } from '../../../Store/Reducers';
import { formatDateFull } from '../../../Utils/Utilities';

const SubscriptionInfo = ({
  open,
  onClose,
}) => {
  const [invoiceData, setInvoiceData] = useState();
  const agencyData = useSelector(getAgencyDetails);
  const expiryDT = agencyData?.SubTillDT;

  // Fetch data when modal opens
  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      try {
        const data = await getSubscriptionData();
        if (data?.BZInvoices) {
          setInvoiceData(JSON.parse(data.BZInvoices));
        } else {
          setInvoiceData([]);
        }
      } catch (error) {
        console.error('Failed to fetch subscription data:', error);
        setInvoiceData([]);
      }
    }
    fetchData();
  }, [open]);

  // Sort invoice data by InvDT in descending order
  const sortedInvoices = useMemo(() => {
    if (!invoiceData || !Array.isArray(invoiceData)) return [];

    return [...invoiceData].sort((a, b) => {
      const dateA = new Date(a.InvDT);
      const dateB = new Date(b.InvDT);
      return dateB - dateA; // Descending order
    });
  }, [invoiceData]);

  const handleClose = () => {
    onClose();
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      onClose();
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };

  // Format amount with currency
  const formatAmount = (amount, currency) => {
    try {
      const numAmount = parseFloat(amount);
      return `${currency}${numAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    } catch (error) {
      return `${currency}${amount}`;
    }
  };

  // Handle invoice link click
  const handleInvoiceClick = (invoice) => {
    if (invoice.InvLink) {
      // Open the invoice link in a new window/tab
      window.open(invoice.InvLink, '_blank', 'noopener,noreferrer');

      // Trigger print dialog after a short delay to allow page to load
      setTimeout(() => {
        const printWindow = window.open(invoice.InvLink, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
      }, 1000);
    }
  };

  // Render invoice list
  const renderInvoiceList = () => (
    <div className="p-0">
      {expiryDT && (
        <div className="flex justify-start pl-4 mb-4">
          <div className="inline-flex items-center gap-1.5 px-3 py-2 text-sahi-red bg-sahi-red-light sahi-radius">
            <span className="text-sm">
              {new Date(expiryDT) < new Date() ? 'Your subscription expired on' : 'Your subscription expires on'}
            </span>
            <span className="text-sm font-bold ">
              {formatDateFull(expiryDT)}
            </span>
          </div>
        </div>
      )}
      <div className="overflow-auto px-4 lg:max-h-[70vh] flex flex-col custom-scroll">
        {sortedInvoices.length > 0 ? (
          <div className="space-y-3">
            {sortedInvoices.map((invoice, index) => (
              <div
                key={`${invoice.InvNo}-${index}`}
                className="bg-white shadow-sm border border-gray-200 sahi-radius p-4 w-full transition-shadow duration-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    {/* Invoice Number and action button */}
                    <div className="flex justify-between items-center gap-3 flex-wrap">
                      <h3 className="font-medium text-sub-text-color">
                        Invoice <span className='text-text-color'>#{invoice.InvNo}</span>
                      </h3>
                      <div className="flex-shrink-0">
                        {invoice.InvLink ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleInvoiceClick(invoice)}
                            className="h-8 px-3 text-xs transition-colors"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View & Print
                          </Button>
                        ) : (
                          <div className="flex items-center text-xs text-sub-text-color">
                            <FileText className="h-3 w-3 mr-1" />
                            No Invoice Link
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className='flex justify-between items-center'>
                      <div className="text-sm text-sub-text-color">
                        {formatDate(invoice.InvDT)}
                      </div>
                      <div className="text-lg font-semibold text-sahi-primary">
                        {formatAmount(invoice.InvAmt, invoice.InvCurr)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="w-fit text-sm text-sub-text-color lg:whitespace-nowrap bg-sahi-white sahi-radius px-5 py-2 border">
            No invoices found.
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl w-full">
        {/* <DialogHeader>
          <DialogTitle className="font-semibold text-white">
            Subscription Information
          </DialogTitle>
          <div className="flex items-center gap-2">
            <DialogClose
              className="h-9 w-9 p-0 text-white hover:bg-white/20 flex items-center justify-center"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader> */}
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">
                  Subscription Information
                </div>
              </DialogTitle>
            </div>
          </div>
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            Subscription Information
          </DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose className="flex-shrink-0" tabIndex={-1}>
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* Invoice List */}
        {renderInvoiceList()}

        {/* Footer */}
        <div className="flex justify-start px-4 pb-4">
          <Button
            variant="outline"
            onClick={handleClose}
            className="sahi-secondary-button sm:flex-none sm:min-w-[100px]"
          >
            <X className="w-3 h-3" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionInfo;